import { Chess } from "@vendor/chess.js";

export type DiagramClash = {
  /** Enemy pieces, other than the king, that the moved piece now attacks. */
  hits: string[];
  /** The enemy king, when this position is check. */
  checks: string[];
  /** The square where a piece was captured, when this move took one. */
  captured: string | null;
};

type VerboseMove = {
  from: string;
  to: string;
  flags?: string;
  captured?: string;
};

/**
 * The contact this move created: a capture, a piece it now attacks, or check.
 * A developing move that hits nothing is an empty result, so the diagram stays
 * quiet until there is a real clash.
 */
export function diagramClash(
  chess: Chess,
  played: { from: string; to: string; captured?: string; flags?: string }
): DiagramClash {
  const hits = new Set<string>();
  const checks = new Set<string>();
  const captured = captureSquare(played);
  for (const move of attacksFrom(chess, played.to)) {
    if (!move.captured) continue;
    const square = enPassantVictim(move) ?? move.to;
    if (square === captured) continue;
    const piece = chess.get(square);
    if (piece?.type === "k") checks.add(square);
    else hits.add(square);
  }
  if (chess.inCheck()) {
    const king = kingSquare(chess, chess.turn());
    if (king) checks.add(king);
  }
  return {
    hits: [...hits].sort(),
    checks: [...checks].sort(),
    captured,
  };
}

/** One legend line. Empty when the move created no contact. */
export function clashCaption(clash: DiagramClash): string {
  const parts: string[] = [];
  if (clash.captured) parts.push(`Captures on ${clash.captured}`);
  if (clash.hits.length) parts.push(`Attacks ${clash.hits.join(", ")}`);
  if (clash.checks.length) parts.push(`Checks ${clash.checks.join(", ")}`);
  return parts.join(". ");
}

/** Legal captures by the piece that just moved. It is the other side's turn. */
function attacksFrom(chess: Chess, square: string): VerboseMove[] {
  const piece = chess.get(square);
  if (!piece) return [];
  const parts = chess.fen().split(" ");
  parts[1] = piece.color;
  parts[3] = "-";
  const probe = new Chess(parts.join(" "));
  return probe.moves({ square, verbose: true }) as VerboseMove[];
}

function captureSquare(move: {
  from: string;
  to: string;
  captured?: string;
  flags?: string;
}): string | null {
  if (!move.captured) return null;
  return move.flags?.includes("e") ? `${move.to[0]}${move.from[1]}` : move.to;
}

function enPassantVictim(move: VerboseMove): string | null {
  if (!move.flags?.includes("e")) return null;
  return `${move.to[0]}${move.from[1]}`;
}

function kingSquare(chess: Chess, color: "w" | "b"): string | null {
  const board = chess.board();
  for (let rank = 0; rank < 8; rank += 1) {
    for (let file = 0; file < 8; file += 1) {
      const piece = board[rank][file];
      if (piece?.type === "k" && piece.color === color) {
        return `${"abcdefgh"[file]}${8 - rank}`;
      }
    }
  }
  return null;
}
